<script setup>
import { ref, onMounted, computed } from 'vue'; // Import 'computed'
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const locations = ref([]);
// Use column names from dbo.location schema for form fields
const newLocation = ref({ loc_no: '', loc_desc: '', dep_no: '', loc_crit_no: '', site_no: '', note_tech: '' });
const editingLocation = ref(null); // Holds the location being edited

// Added selectedLocation ref to track the row clicked in the table for Edit/Delete
const selectedLocation = ref(null);

// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!

const locationsApiUrl = `${API_BASE_URL}/api/locations`;
const departmentsApiUrl = `${API_BASE_URL}/api/departments`; // Need to fetch departments for dropdown
const sitesApiUrl = `${API_BASE_URL}/api/sites`; // Need to fetch sites for dropdown

const departments = ref([]); // To hold the list of departments for the dropdown
const sites = ref([]); // To hold the list of sites for the dropdown

const loading = ref(false); // Add loading state
const error = ref(null); // Add error state
const notification = ref({ // Add notification state
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model ---
// This computed property handles the Location Number input (loc_no)
const currentLocNo = computed({
  get() {
    // If editing, get the loc_no from editingLocation, otherwise from newLocation
    return editingLocation.value ? editingLocation.value.loc_no : newLocation.value.loc_no;
  },
  set(value) {
    // If editing, update the loc_no in editingLocation, otherwise in newLocation
    if (editingLocation.value) {
      editingLocation.value.loc_no = value;
    } else {
      newLocation.value.loc_no = value;
    }
  }
});

// This computed property handles the Location Description input (loc_desc)
const currentLocDesc = computed({
  get() {
    // If editing, get the loc_desc from editingLocation, otherwise from newLocation
    return editingLocation.value ? editingLocation.value.loc_desc : newLocation.value.loc_desc;
  },
  set(value) {
    if (editingLocation.value) {
      editingLocation.value.loc_desc = value;
    } else {
      newLocation.value.loc_desc = value;
    }
  }
});

// This computed property handles the Department Number dropdown (dep_no)
const currentDepNo = computed({
  get() {
    return editingLocation.value ? editingLocation.value.dep_no : newLocation.value.dep_no;
  },
  set(value) {
    if (editingLocation.value) {
      editingLocation.value.dep_no = value;
    } else {
      newLocation.value.dep_no = value;
    }
  }
});

// This computed property handles the Location Criticality Number input (loc_crit_no)
const currentLocCritNo = computed({
  get() {
    return editingLocation.value ? editingLocation.value.loc_crit_no : newLocation.value.loc_crit_no;
  },
  set(value) {
    if (editingLocation.value) {
      editingLocation.value.loc_crit_no = value;
    } else {
      newLocation.value.loc_crit_no = value;
    }
  }
});

// This computed property handles the Site Number dropdown (site_no)
const currentSiteNo = computed({
  get() {
    return editingLocation.value ? editingLocation.value.site_no : newLocation.value.site_no;
  },
  set(value) {
    if (editingLocation.value) {
      editingLocation.value.site_no = value;
    } else {
      newLocation.value.site_no = value;
    }
  }
});

// This computed property handles the Technical Note input (note_tech)
const currentNoteTech = computed({
  get() {
    return editingLocation.value ? editingLocation.value.note_tech : newLocation.value.note_tech;
  },
  set(value) {
    if (editingLocation.value) {
      editingLocation.value.note_tech = value;
    } else {
      newLocation.value.note_tech = value;
    }
  }
});

// --- End Computed Properties ---


// Fetch all locations
const fetchLocations = async () => {
  loading.value = true;
  error.value = null;
  selectedLocation.value = null; // Deselect any selected location when refreshing
  try {
    const response = await axios.get(locationsApiUrl);
    locations.value = response.data;
  } catch (err) {
    console.error('Error fetching locations:', err);
    error.value = 'Failed to load locations. Please try again later.';
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Fetch departments for the dropdown
const fetchDepartments = async () => {
     // This fetch is less critical than locations, handle errors internally
    try {
        // Assuming your department API returns data like [{ dep_no: 'D001', dep_desc: 'Department A', ... }]
        // We need dep_no for the value and dep_desc for the display text
        const response = await axios.get(departmentsApiUrl);
        departments.value = response.data.map(dep => ({
            dep_no: dep.dep_no, // Use dep_no as the value
            dep_desc: dep.dep_desc // Use dep_desc as the display text
        }));
        if (departments.value.length === 0) {
            console.warn('No departments fetched.');
        }
    } catch (err) {
        console.error('Error fetching departments:', err);
        showNotification('Could not load departments list for dropdown.', 'warning');
    }
};

// Fetch sites for the dropdown
const fetchSites = async () => {
     // This fetch is less critical than locations, handle errors internally
    try {
        // Assuming your site API returns data like [{ SiteCode: 'S001', Sitename: 'Site A', ... }]
        // We need SiteCode for the value and Sitename for the display text
        const response = await axios.get(sitesApiUrl);
        sites.value = response.data.map(site => ({
            SiteCode: site.SiteCode, // Use SiteCode as the value
            Sitename: site.Sitename // Use Sitename as the display text
        }));
        if (sites.value.length === 0) {
            console.warn('No sites fetched.');
        }
    } catch (err) {
        console.error('Error fetching sites:', err);
        showNotification('Could not load sites list for dropdown.', 'warning');
    }
};


// Add a new location
const createLocation = async () => {
  // Use newLocation.value directly as the form is bound to it when not editing
  if (!newLocation.value.loc_no.trim() || !newLocation.value.loc_desc.trim() || !newLocation.value.dep_no || !newLocation.value.site_no) {
    showNotification('Location Number, Description, Department, and Site are required.', 'warning');
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.post(locationsApiUrl, newLocation.value);
    locations.value.push(response.data);
    // Clear form - include all fields
    newLocation.value = { loc_no: '', loc_desc: '', dep_no: '', loc_crit_no: '', site_no: '', note_tech: '' };
    showNotification('Location created successfully!', 'success');
  } catch (err) {
    console.error('Error creating location:', err);
    if (err.response?.status === 409) {
      error.value = err.response.data || 'Location Number already exists.';
    } else if (err.response?.status === 400) {
       error.value = err.response.data || 'Bad Request: Invalid Department Number or Site Number.';
    }
    else {
      error.value = 'Failed to create location.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Select a location from the table
const selectLocation = (location) => {
  selectedLocation.value = location;
   // If you select a location while editing, stop editing
    if (editingLocation.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors
}

// Start editing
const startEditing = () => {
  if (selectedLocation.value) {
      editingLocation.value = { ...selectedLocation.value };
      // When starting edit, clear newLocation form and any errors
      newLocation.value = { loc_no: '', loc_desc: '', dep_no: '', loc_crit_no: '', site_no: '', note_tech: '' };
      error.value = null;
  } else {
       showNotification('Please select a location to edit.', 'warning');
  }
};

// Cancel editing
const cancelEditing = () => {
  editingLocation.value = null;
  // When canceling edit, clear newLocation form and any errors
  newLocation.value = { loc_no: '', loc_desc: '', dep_no: '', loc_crit_no: '', site_no: '', note_tech: '' };
  error.value = null;
};

// Update location
const updateLocation = async () => {
  // Use editingLocation.value directly as the form is bound to it when editing
   if (!editingLocation.value.loc_desc.trim() || !editingLocation.value.dep_no || !editingLocation.value.site_no) {
    showNotification('Location Description, Department, and Site are required.', 'warning');
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    // PUT request uses loc_no in the URL
    const response = await axios.put(`${locationsApiUrl}/${editingLocation.value.loc_no}`, editingLocation.value);

    const index = locations.value.findIndex(l => l.loc_no === editingLocation.value.loc_no);
    if (index !== -1) {
      // Update the location in the list with the data from the response
      locations.value[index] = { ...locations.value[index], ...response.data };
    }
     // If the updated location was selected, keep it selected with updated data
     if (selectedLocation.value && selectedLocation.value.loc_no === editingLocation.value.loc_no) {
         selectedLocation.value = { ...selectedLocation.value, ...response.data };
     }

    editingLocation.value = null; // Exit editing mode
    showNotification('Location updated successfully!', 'success');
  } catch (err) {
    console.error('Error updating location:', err);
    if (err.response?.status === 404) {
      error.value = 'Location not found.';
    } else if (err.response?.status === 400) {
        error.value = err.response.data || 'Bad Request: Invalid Department Number or Site Number during update.';
    }
    else {
      error.value = 'Failed to update location.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Delete location
const deleteLocation = async () => {
  if (!selectedLocation.value) {
       showNotification('Please select a location to delete.', 'warning');
       return;
  }
  const locNoToDelete = selectedLocation.value.loc_no;

  if (confirm(`Are you sure you want to delete location ${locNoToDelete}? This may also delete related records in other tables (e.g., assets).`)) {
    loading.value = true;
    error.value = null;
    try {
      // DELETE request uses loc_no in the URL
      await axios.delete(`${locationsApiUrl}/${locNoToDelete}`);
      locations.value = locations.value.filter(l => l.loc_no !== locNoToDelete);
      // If the deleted location was selected or being edited, clear states
      selectedLocation.value = null;
      if(editingLocation.value && editingLocation.value.loc_no === locNoToDelete) {
          editingLocation.value = null;
      }
      showNotification('Location deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting location:', err);
      if (err.response?.status === 404) {
        error.value = 'Location not found.';
      } else if (err.response?.status === 409) {
         // Specific error message from the backend about FK conflicts elsewhere
         error.value = err.response.data || 'Conflict: Cannot delete location because it is referenced by other entities (e.g., assets). You must delete them first.'; // Removed the extra parenthesis here
      }
      else {
        error.value = 'Failed to delete location.';
      }
      showNotification(error.value, 'error');
    } finally {
      loading.value = false;
    }
  }
};

// Show notification helper function
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000); // Hide after 5 seconds
};


// Fetch data on mount
onMounted(() => {
    fetchLocations();
    fetchDepartments(); // Fetch departments for the dropdown
    fetchSites(); // Fetch sites for the dropdown
});
</script>

<template>
  <div class="sites-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingLocation ? 'Edit Location' : 'Add New Location' }}</h2>
        <form @submit.prevent="editingLocation ? updateLocation() : createLocation()">
          <div class="form-group">
            <label for="loc_no">Location Number:</label>
             <input type="text" id="loc_no" v-model="currentLocNo" required
                   :disabled="editingLocation !== null || loading">
          </div>
          <div class="form-group">
            <label for="loc_desc">Description:</label>
             <input type="text" id="loc_desc" v-model="currentLocDesc" required
                   :disabled="loading">
          </div>
           <div class="form-group">
              <label for="dep_no">Department:</label>
               <select id="dep_no" v-model="currentDepNo" required :disabled="loading">
                 <option value="" disabled>Select Department</option>
                <option v-for="department in departments" :key="department.dep_no" :value="department.dep_no">
                    {{ department.dep_desc }} ({{ department.dep_no }})
                </option>
                 <option value="" v-if="departments.length === 0 && !loading">No departments available</option>
              </select>
            </div>
             <div class="form-group">
              <label for="site_no">Site:</label>
               <select id="site_no" v-model="currentSiteNo" required :disabled="loading">
                 <option value="" disabled>Select Site</option>
                <option v-for="site in sites" :key="site.SiteCode" :value="site.SiteCode">
                    {{ site.Sitename }} ({{ site.SiteCode }})
                </option>
                 <option value="" v-if="sites.length === 0 && !loading">No sites available</option>
              </select>
            </div>
             <div class="form-group">
              <label for="loc_crit_no">Criticality Number:</label>
               <input type="text" id="loc_crit_no" v-model="currentLocCritNo"
                     :disabled="loading">
            </div>
             <div class="form-group">
              <label for="note_tech">Technical Note:</label>
               <input type="text" id="note_tech" v-model="currentNoteTech"
                     :disabled="loading">
            </div>


          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingLocation ? 'Update Location' : 'Save Location' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingLocation" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Location List</h2>

         <div v-if="loading && locations.length === 0">Loading locations...</div>
         <div v-if="error && locations.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
           <button @click="addNew" :disabled="loading">Add New</button>
           <button @click="startEditing" :disabled="!selectedLocation || loading">Edit</button>
           <button @click="deleteLocation" :disabled="!selectedLocation || loading">Delete</button>
           <button @click="fetchLocations" :disabled="loading">Refresh</button>
         </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Loc No</th>
              <th>Description</th>
              <th>Dept No</th>
              <th>Site No</th>
              <th>Criticality No</th>
              <th>Technical Note</th>
              </tr>
          </thead>
          <tbody>
            <tr v-for="location in locations" :key="location.loc_no"
                :class="{ 'selected': selectedLocation && selectedLocation.loc_no === location.loc_no }"
                @click="selectLocation(location)"> <td>{{ location.loc_no }}</td>
              <td>{{ location.loc_desc }}</td>
              <td>{{ location.dep_no }}</td>
              <td>{{ location.site_no }}</td>
              <td>{{ location.loc_crit_no }}</td>
              <td>{{ location.note_tech }}</td>
              </tr>
             <tr v-if="locations.length === 0 && !loading">
                <td colspan="6" style="text-align: center;">No locations found.</td>
             </tr>
          </tbody>
        </table>
      </div>
    </div>

     <div v-if="notification.show" :class="['notification', notification.type]">
      {{ notification.message }}.
    </div>

  </div>
</template>

<style scoped>
/* Copying the exact styles from your SitesView */
.sites-page {
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f4f6f8;
}

.container {
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap; /* Allow wrapping on small screens */
}

.form-panel {
  flex: 1; /* Form panel takes 1 part of the available space */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
}

.table-panel {
  flex: 2; /* Table panel takes 2 parts of the available space (making it wider) */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
  overflow-x: auto; /* Add horizontal scroll if table content is too wide */
}


h2 {
  color: #333;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  color: #555;
  font-weight: bold;
}

input[type="text"],
input[type="number"],
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
  border-color: #007bff;
  outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-actions {
  margin-top: 20px;
}

.save-button,
.cancel-button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.save-button {
  background-color: #28a745; /* Green */
  color: white;
  margin-right: 10px;
}

.save-button:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-button {
  background-color: #6c757d; /* Gray */
  color: white;
}

.cancel-button:hover:not(:disabled) {
  background-color: #5a6268;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}


.table-actions {
  margin-bottom: 15px;
  display: flex;
  gap: 10px; /* Space between buttons */
  flex-wrap: wrap;
}

.table-actions button {
  padding: 8px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background-color: #007bff; /* Blue */
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease, opacity 0.3s ease;
}

/* Specific button styles from SitesView */
.table-actions button:nth-child(2) { /* Edit button */
  background-color: #ffc107; /* Yellow */
  border-color: #ffc107;
  color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
  background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
  background-color: #dc3545; /* Red */
  border-color: #dc3545;
  color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
  background-color: #c82333;
}


.table-actions button:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}


.site-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.9rem;
}

/* Increased padding for bigger cells */
.site-table th,
.site-table td {
  padding: 12px; /* Increased from 10px */
  border: 1px solid #ddd;
  text-align: left;
}

.site-table th {
  background-color: #e9ecef; /* Light gray */
  font-weight: bold;
  color: #495057;
}

.site-table tbody tr:hover {
  background-color: #f8f9fa; /* Lighter gray on hover */
  cursor: pointer;
}

.site-table tbody tr.selected {
  background-color: #cfe2ff; /* Light blue when selected */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .form-panel,
  .table-panel {
    margin-bottom: 20px;
    min-width: unset; /* Remove min-width on small screens */
    flex: unset; /* Remove flex on small screens */
    width: 100%; /* Make panels full width */
  }

  .table-actions {
    flex-direction: column; /* Stack buttons vertically */
    gap: 5px;
  }

  .table-actions button {
    width: 100%; /* Make buttons full width */
    margin-right: 0;
  }
}

/* Notification Styling */
.notification {
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: opacity 0.5s ease-in-out;
}

.notification.success {
  background-color: #28a745; /* Green */
}

.notification.error {
  background-color: #dc3545; /* Red */
}

.notification.warning {
  background-color: #ffc107; /* Yellow */
  color: #212529;
}

.error-message {
  color: #dc3545;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #dc3545;
  background-color: #fff3f3;
  border-radius: 4px;
}

</style>
